import React from 'react';
import Table from '../../components/table';
import { UserManagementSkeleton } from '../../components/Skeleton';
import { SortConfig, MechanicalContractor } from './types';
import { formatAddressForDisplay, formatLocalsForDisplay,formatTradesForDisplay } from './utils';
import StatusPill from '../StatusPill';

interface MechanicalContractorTableProps {
  loading: boolean;
  mechanicalContractors: MechanicalContractor[];
  totalItems: number;
  currentPage: number;
  pageSize: number;
  sortConfig: SortConfig;
  searchTerm: string;
  handleSearch: (value: string) => void;
  handleRowClick: (item: MechanicalContractor) => void;
  handleSort: (key: string, direction: 'asc' | 'desc' | null) => void;
  setCurrentPage: (page: number) => void;
}

const MechanicalContractorTable: React.FC<MechanicalContractorTableProps> = ({
  loading,
  mechanicalContractors,
  totalItems,
  currentPage,
  pageSize,
  sortConfig,
  searchTerm,
  handleSearch,
  handleRowClick,
  handleSort,
  setCurrentPage
}) => {
  // Define table columns - Name, Address, Locals, Status
  const columns = [
    {
      label: 'Name',
      accessor: 'name',
      sortable: true,
      minWidth: '250px',
      render: (value: string) => {
        const fullName = value || '';
        const displayName = fullName.length > 50 ? fullName.substring(0, 50) + '...' : fullName;
        return <span title={fullName}>{displayName}</span>;
      }
    },
    {
      label: 'Address',
      accessor: 'address',
      sortable: false,
      minWidth: '300px',
      render: (value: any, row: MechanicalContractor) => {
        const { display, full } = formatAddressForDisplay(row);
        return <span title={full}>{display}</span>;
      }
    },
    {
      label: 'Locals',
      accessor: 'locals',
      sortable: false,
      minWidth: '180px',
      render: (value: any, row: MechanicalContractor) => {
        const { display, full } = formatLocalsForDisplay(row);
        return <span title={full}>{display}</span>;
      }
    },
    {
      label: 'Trades',
      accessor: 'contractorTrades',
      sortable: false,
      minWidth: '180px',
      render: (value: any, row: MechanicalContractor) => {
        const { display, full } = formatTradesForDisplay(row);
        return <span title={full}>{display}</span>;
      }
    },
    {
      label: 'Status',
      accessor: 'status',
      sortable: true,
      minWidth: '120px',
      render: (value: string) => <StatusPill status={value} />
    },
  ];

  return (
    <>
      {loading ? (
        <UserManagementSkeleton />
      ) : (
        <div className="bg-white rounded-card shadow overflow-hidden p-3 sm:p-4">
          <div className="relative min-h-[300px]">
            {mechanicalContractors.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <svg className="w-12 h-12 text-text-secondary mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h3 className="text-subtext font-medium text-text-primary mb-1">No Mechanical Contractors Found</h3>
                <p className="text-small text-text-secondary">No mechanical contractors match your search criteria</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table
                  testId="mechanical-contractors-table"
                  columns={columns}
                  data={mechanicalContractors}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  searchable={true}
                  onSearch={handleSearch}
                  onRowClick={handleRowClick}
                  makeRowClickable={true}
                  onSort={handleSort}
                  sortConfig={sortConfig}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MechanicalContractorTable;